import type { BoxEffects, BoxGlow, BoxShadow, BoxStroke } from '@lite-workspace/dto-fotoclass-templates';
import type {
  DrawableEffect,
  DrawableGlowEffect,
  DrawableShadowEffect,
  DrawableStrokeEffect,
} from '../../interfaces/drawable-data.interface';

function toDrawableShadow(shadow: BoxShadow): DrawableShadowEffect {
  return {
    ...shadow,
    kind: 'shadow',
    color: shadow.color,
    blur: shadow.blur,
    offsetX: shadow.offsetX,
    offsetY: shadow.offsetY,
    opacity: shadow.opacity,
    inner: shadow.inner,
    spread: shadow.spread,
    blendMode: shadow.blendMode,
    // `enabled === false` skips the effect on render (ps-effects effect-layers.ts), so losing the flag
    // switches a designer-disabled shadow back on.
    enabled: shadow.enabled,
  };
}

function toDrawableStroke(stroke: BoxStroke): DrawableStrokeEffect {
  return {
    ...stroke,
    kind: 'stroke',
    color: stroke.color,
    width: stroke.width,
    opacity: stroke.opacity,
    position: stroke.position,
    // Тип углов — такое же решение автора, как позиция и толщина: без него выбор «Скруглённые»
    // не переживал круг через drawable-форму и на перезагрузке возвращался к острым.
    corners: stroke.corners,
    enabled: stroke.enabled,
  };
}

function toDrawableGlow(glow: BoxGlow): DrawableGlowEffect {
  return { kind: 'glow', ...glow };
}

function toBoxShadow(effect: DrawableShadowEffect): BoxShadow {
  const { kind: _kind, ...fields } = effect;
  return {
    ...fields,
    color: effect.color,
    blur: effect.blur,
    offsetX: effect.offsetX,
    offsetY: effect.offsetY,
    opacity: effect.opacity,
    inner: effect.inner,
    spread: effect.spread,
    blendMode: effect.blendMode,
    enabled: effect.enabled,
  };
}

function toBoxStroke(effect: DrawableStrokeEffect): BoxStroke {
  const { kind: _kind, ...fields } = effect;
  return {
    ...fields,
    color: effect.color,
    width: effect.width,
    opacity: effect.opacity,
    position: effect.position,
    corners: effect.corners,
    enabled: effect.enabled,
  };
}

function toBoxGlow(effect: DrawableGlowEffect): BoxGlow {
  const { kind: _kind, ...glow } = effect;
  return glow;
}

/**
 * Live styles belong to drawable.effects so text/font edits cannot discard their source parameters.
 * Shadow/stroke controls use the complete shared DTO; older raw snapshots remain readable during migration.
 */
export function buildDrawableEffects(effects: BoxEffects | undefined): readonly DrawableEffect[] | undefined {
  if (!effects) return undefined;
  const shadows = (effects.shadows ?? []).map(toDrawableShadow);
  const strokes = (effects.strokes ?? []).map(toDrawableStroke);
  const glows = (effects.glows ?? []).map(toDrawableGlow);
  const result: DrawableEffect[] = [...shadows, ...strokes, ...glows];
  if (effects.bevel) result.push({ kind: 'bevel', ...effects.bevel });
  if (effects.satin) result.push({ kind: 'satin', ...effects.satin });
  if (effects.patternOverlay) result.push({ kind: 'patternOverlay', ...effects.patternOverlay });
  // Same-kind entries keep Photoshop list order; the first one maps back to the single DTO slot.
  for (const overlay of [effects.colorOverlay, ...(effects.extraColorOverlays ?? [])])
    if (overlay?.rendering === 'live') result.push({ kind: 'colorOverlay', ...overlay });
  for (const overlay of [effects.gradientOverlay, ...(effects.extraGradientOverlays ?? [])])
    if (overlay?.rendering === 'live') result.push({ kind: 'gradientOverlay', ...overlay });
  return result.length > 0 ? result : undefined;
}

/**
 * Typed effects carry the full DTO (`contour`, `fillType`/`gradient` included); an older drawable saved without
 * them gets them back from the raw snapshot UNDER the typed values (typed wins on every field it carries), paired by
 * position. Positional pairing is only sound while the editor has not added or removed effects of that
 * kind: when the counts differ there is no way to tell which raw entry belongs to which live one, so the
 * untyped fields are dropped rather than attached to a neighbour's effect.
 */
function keepUntypedFields<T extends object>(typed: readonly T[], raw: readonly T[] | undefined): T[] {
  if (raw === undefined || raw.length !== typed.length) return [...typed];
  return typed.map((effect, index) => ({ ...raw[index], ...effect }));
}

/**
 * Inverse of `buildDrawableEffects` — typed live effects, including marked overlays, are authoritative.
 * Only unmarked legacy overlays remain raw import-resolved passthrough. Albums saved before glows became typed
 * carry `box.effects.glows` in the persisted v1 form;
 * the first decode after this change reads it into the typed side — not dropped (whether it is drawn is the
 * scene's `enabled` filter, not the codec's call). `drawableEffects === undefined` means "this side has
 * nothing to say" (defers to rawEffects unchanged) rather than "clear the effect" — decode never hands back
 * an empty array, only `undefined`.
 */
export function mergeDrawableEffects(
  drawableEffects: readonly DrawableEffect[] | undefined,
  rawEffects: BoxEffects | undefined,
  ownership?: 'typed',
): BoxEffects | undefined {
  if (ownership === 'typed') {
    rawEffects = undefined;
    drawableEffects ??= [];
  }

  if (drawableEffects === undefined) return rawEffects;

  const shadows = drawableEffects.filter((e): e is DrawableShadowEffect => e.kind === 'shadow').map(toBoxShadow);
  const strokes = drawableEffects.filter((e): e is DrawableStrokeEffect => e.kind === 'stroke').map(toBoxStroke);
  const glows = drawableEffects.filter((e): e is DrawableGlowEffect => e.kind === 'glow').map(toBoxGlow);

  const merged: { -readonly [K in keyof BoxEffects]?: BoxEffects[K] } = { ...rawEffects };
  const [gradientOverlay, ...extraGradientOverlays] = drawableEffects
    .filter(e => e.kind === 'gradientOverlay')
    .map(({ kind: _kind, ...fields }) => fields);
  if (gradientOverlay) merged.gradientOverlay = gradientOverlay;
  else if (rawEffects?.gradientOverlay?.rendering === 'live') delete merged.gradientOverlay;
  if (extraGradientOverlays.length > 0) merged.extraGradientOverlays = extraGradientOverlays;
  else if (rawEffects?.extraGradientOverlays?.some(o => o.rendering === 'live')) delete merged.extraGradientOverlays;
  const [colorOverlay, ...extraColorOverlays] = drawableEffects
    .filter(e => e.kind === 'colorOverlay')
    .map(({ kind: _kind, ...fields }) => fields);
  if (colorOverlay) merged.colorOverlay = colorOverlay;
  else if (rawEffects?.colorOverlay?.rendering === 'live') delete merged.colorOverlay;
  if (extraColorOverlays.length > 0) merged.extraColorOverlays = extraColorOverlays;
  else if (rawEffects?.extraColorOverlays?.some(o => o.rendering === 'live')) delete merged.extraColorOverlays;
  const bevel = drawableEffects.find(e => e.kind === 'bevel');
  // Older drawable documents kept bevel only in rawEffects; a typed value supersedes that snapshot.
  if (bevel) {
    const { kind: _kind, ...fields } = bevel;
    merged.bevel = fields;
  }

  for (const kind of ['satin', 'patternOverlay'] as const) {
    const effect = drawableEffects.find(e => e.kind === kind);
    if (effect?.kind === 'satin') {
      const { kind: _kind, ...fields } = effect;
      merged.satin = fields;
    } else if (effect?.kind === 'patternOverlay') {
      const { kind: _kind, ...fields } = effect;
      merged.patternOverlay = fields;
    }
  }

  if (shadows.length > 0) merged.shadows = keepUntypedFields(shadows, rawEffects?.shadows);
  else delete merged.shadows;
  if (strokes.length > 0) merged.strokes = keepUntypedFields(strokes, rawEffects?.strokes);
  else delete merged.strokes;
  if (glows.length > 0) merged.glows = keepUntypedFields(glows, rawEffects?.glows);
  else delete merged.glows;

  return Object.keys(merged).length > 0 ? (merged as BoxEffects) : undefined;
}
